'use client';

import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useSyncExternalStore,
    type ComponentPropsWithRef,
    type MouseEvent,
    type ReactElement,
    type ReactNode,
} from 'react';
import { cn, resolveAssetUrl } from '@/lib/utils';
import { Button } from '@/shared-ui/atoms/button';
import { Icon } from '@/shared-ui/atoms/icon';
import { Typography } from '@/shared-ui/atoms/typography';
import { CloseIcon } from '@/shared-ui/icons/close';
import { useTranslation } from 'react-i18next';
import { getAppDownloadUrl } from './app-promo-strip.shared';
import { appPromoDismissalStore } from './app-promo-strip-dismissal';

// Self-hosted: the original demandware.static origin is not in the CSP `img-src`.
export const APP_PROMO_ICON_URL = '/images/app-download/app-icon.svg';
export const APP_PROMO_LOGO_URL = '/images/app-download/snk-app.svg';

type RootProps = Omit<ComponentPropsWithRef<'div'>, 'children'> & {
    children: ReactNode;
    onClose?: () => void;
    onDownloadRedirect?: (url: string) => void;
    iosAppDownloadUrl?: string;
    androidAppDownloadUrl?: string;
    /** Resolved server-side; avoids the `navigator` read disagreeing across hydration. */
    downloadUrl?: string;
};

type ContentProps = ComponentPropsWithRef<'div'>;
type AppIconProps = Omit<ComponentPropsWithRef<'img'>, 'src' | 'alt'>;
type LogoProps = Omit<ComponentPropsWithRef<'img'>, 'src' | 'alt'>;
type MessageProps = Omit<ComponentPropsWithRef<typeof Typography>, 'variant'>;
type CloseButtonProps = Omit<
    ComponentPropsWithRef<typeof Button>,
    'children' | 'type' | 'size' | 'variant' | 'price' | 'originalPrice'
>;
type DownloadButtonProps = Omit<ComponentPropsWithRef<'a'>, 'children' | 'href'> & {
    children?: ReactNode;
};

export type AppPromoStripRootProps = RootProps;

type AppPromoStripContextValue = {
    handleClose: () => void;
    handleDownload: (event: MouseEvent<HTMLAnchorElement>) => void;
    downloadUrl: string;
    iconLabel: string;
    logoLabel: string;
    downloadLabel: string;
    closeLabel: string;
};

const AppPromoStripContext = createContext<AppPromoStripContextValue | null>(null);

const Root = ({
    onClose,
    onDownloadRedirect,
    iosAppDownloadUrl,
    androidAppDownloadUrl,
    downloadUrl: downloadUrlProp,
    children,
    className,
    ref,
    ...props
}: RootProps): ReactElement | null => {
    const isDismissed = useSyncExternalStore(
        appPromoDismissalStore.subscribe,
        appPromoDismissalStore.getSnapshot,
        appPromoDismissalStore.getServerSnapshot
    );
    const { t } = useTranslation('app');

    const handleClose = useCallback(() => {
        appPromoDismissalStore.dismiss();
        onClose?.();
    }, [onClose]);

    const downloadUrl = useMemo(() => {
        if (downloadUrlProp) {
            return downloadUrlProp;
        }
        const userAgent = typeof navigator === 'undefined' ? '' : navigator.userAgent;
        return getAppDownloadUrl(userAgent, {
            iosAppDownloadUrl,
            androidAppDownloadUrl,
        });
    }, [androidAppDownloadUrl, downloadUrlProp, iosAppDownloadUrl]);

    const handleDownload = useCallback(
        (event: MouseEvent<HTMLAnchorElement>) => {
            if (!onDownloadRedirect) {
                return;
            }
            event.preventDefault();
            onDownloadRedirect(downloadUrl);
        },
        [downloadUrl, onDownloadRedirect]
    );

    if (isDismissed) {
        return null;
    }

    return (
        <AppPromoStripContext.Provider
            value={{
                handleClose,
                handleDownload,
                downloadUrl,
                iconLabel: t('icon'),
                logoLabel: t('logo'),
                downloadLabel: t('download'),
                closeLabel: t('close'),
            }}>
            <div
                ref={ref}
                data-slot="app-promo-strip"
                className={cn('w-full border-b border-border bg-background px-4 py-2 text-foreground', className)}
                {...props}>
                <div className="mx-auto flex w-full max-w-5xl items-center">{children}</div>
            </div>
        </AppPromoStripContext.Provider>
    );
};
Root.displayName = 'AppPromoStrip.Root';

const Content = ({ className, ref, ...props }: ContentProps): ReactElement => (
    <div ref={ref} data-slot="app-promo-strip-content" className={cn('min-w-0 flex-1', className)} {...props} />
);
Content.displayName = 'AppPromoStrip.Content';

const CloseButton = ({ className, onClick, ...props }: CloseButtonProps): ReactElement => {
    const context = useContext(AppPromoStripContext);

    if (!context) {
        throw new Error('AppPromoStrip.CloseButton must be used within AppPromoStrip.Root.');
    }

    return (
        <Button
            type="button"
            size="icon-sm"
            className={cn(
                'shrink-0 rounded-none bg-transparent pr-4 text-foreground hover:bg-transparent active:bg-transparent',
                className
            )}
            aria-label={context.closeLabel}
            onClick={(event) => {
                onClick?.(event);
                if (!event.defaultPrevented) {
                    context.handleClose();
                }
            }}
            {...props}>
            <Icon icon={CloseIcon} className="size-4" />
        </Button>
    );
};
CloseButton.displayName = 'AppPromoStrip.CloseButton';

const AppIcon = ({ className, ...props }: AppIconProps): ReactElement => {
    const context = useContext(AppPromoStripContext);

    if (!context) {
        throw new Error('AppPromoStrip.AppIcon must be used within AppPromoStrip.Root.');
    }

    return (
        <img
            src={resolveAssetUrl(APP_PROMO_ICON_URL)}
            alt={context.iconLabel}
            className={cn('md:w-20 pr-2', className)}
            loading="lazy"
            decoding="async"
            {...props}
        />
    );
};
AppIcon.displayName = 'AppPromoStrip.AppIcon';

const Logo = ({ className, ...props }: LogoProps): ReactElement => {
    const context = useContext(AppPromoStripContext);

    if (!context) {
        throw new Error('AppPromoStrip.Logo must be used within AppPromoStrip.Root.');
    }

    return (
        <img
            src={resolveAssetUrl(APP_PROMO_LOGO_URL)}
            alt={context.logoLabel}
            className={cn('w-[96px] mb-1', className)}
            {...props}
        />
    );
};
Logo.displayName = 'AppPromoStrip.Logo';

const Message = ({ className, children, ...props }: MessageProps): ReactElement => {
    const { t } = useTranslation('app');

    return (
        <Typography
            className={cn('font-light text-[10px] leading-3', className)}
            variant="small"
            data-typography="small"
            {...props}>
            {children ?? t('message')}
        </Typography>
    );
};
Message.displayName = 'AppPromoStrip.Message';

const DownloadButton = ({ children, onClick, ...props }: DownloadButtonProps): ReactElement => {
    const context = useContext(AppPromoStripContext);

    if (!context) {
        throw new Error('AppPromoStrip.DownloadButton must be used within AppPromoStrip.Root.');
    }

    return (
        <Button asChild size="sm">
            <a
                href={context.downloadUrl}
                onClick={(event) => {
                    onClick?.(event);
                    if (!event.defaultPrevented) {
                        context.handleDownload(event);
                    }
                }}
                target="_blank"
                rel="noopener noreferrer"
                {...props}>
                {children ?? context.downloadLabel}
            </a>
        </Button>
    );
};
DownloadButton.displayName = 'AppPromoStrip.DownloadButton';

const AppPromoStrip = {
    Root,
    Content,
    CloseButton,
    AppIcon,
    Logo,
    Message,
    DownloadButton,
} as const;

export { AppPromoStrip };
